import { inject, Injectable, signal } from '@angular/core';
import { DEFAULT_THEME_NAME, normalizeThemeName, resolveTheme, ThemeSwitcher } from './ThemeSwitcher';
import { SettingsService } from '../settings.service';


type ThemeStorage = {
  themeName: string;
  themeVariantMode: "Light" | "Dark" | "System";
  defaultThemeVersion?: number;
  // themeVariant: string;
  // useSystemTheme: boolean;
};

const CURRENT_DEFAULT_THEME_VERSION = 1;
const PREVIOUS_PRODUCT_DEFAULT_THEME_NAME = "Template";


@Injectable({
  providedIn: 'root',
})
export class ThemeManagerService {


  themeSwitcher = ThemeSwitcher.getInstance();

  savedTheme: ThemeStorage = {
    themeName: DEFAULT_THEME_NAME,
    themeVariantMode: 'System'
    //useSystemTheme: false,
  };

  savedThemeSignal = signal<ThemeStorage>(this.savedTheme);

  constructor(
    private settingsService: SettingsService,
  ) {

    // These don't work
    // this.router.events.pipe(
    //   filter((event) => event instanceof NavigationEnd),
    //   tap(() => {
    //     console.log('NavigationEnd detected in ThemeManagerService');
    //   })
    // ) 

    // this.activatedRoute.url.subscribe(() => {
    //   console.log('URL changed in ThemeManagerService');
    // });

    const storedTheme = this.readStoredTheme();
    if (storedTheme) {
      this.savedTheme = this.normalizeSavedTheme(storedTheme);
      const inheritedPreviousDefault = storedTheme.defaultThemeVersion !== CURRENT_DEFAULT_THEME_VERSION
        && normalizeThemeName(storedTheme.themeName) === PREVIOUS_PRODUCT_DEFAULT_THEME_NAME;

      if (inheritedPreviousDefault) {
        this.savedTheme.themeName = DEFAULT_THEME_NAME;
      }

      this.savedTheme.defaultThemeVersion = CURRENT_DEFAULT_THEME_VERSION;
      const shouldPersistThemeMigration = this.savedTheme.themeName !== storedTheme.themeName
        || this.savedTheme.themeVariantMode !== storedTheme.themeVariantMode
        || this.savedTheme.defaultThemeVersion !== storedTheme.defaultThemeVersion;
      this.applySavedTheme();

      if (shouldPersistThemeMigration) {
        this.saveTheme();
      }
      else {
        this.savedThemeSignal.set({ ...this.savedTheme });
      }
      return;
    }

    // Use the PHMS light palette on first run; later user selections remain persisted.
    this.savedTheme.themeVariantMode = 'Light';
    this.savedTheme.defaultThemeVersion = CURRENT_DEFAULT_THEME_VERSION;
    this.applySavedTheme();
    this.saveTheme();
  }

  currentStyleName: string = 'no';

  private normalizeSavedTheme(themeStorage: ThemeStorage): ThemeStorage {
    const themeVariantMode = themeStorage.themeVariantMode === "Dark"
      || themeStorage.themeVariantMode === "System"
      ? themeStorage.themeVariantMode
      : "Light";

    return {
      ...themeStorage,
      themeName: normalizeThemeName(themeStorage.themeName),
      themeVariantMode,
    };
  }

  private readStoredTheme(): ThemeStorage | null {
    const savedThemeValue = this.settingsService.getFromLocalStorage('savedTheme');
    if (!savedThemeValue) {
      return null;
    }

    try {
      const parsedTheme = JSON.parse(savedThemeValue) as unknown;
      return parsedTheme && typeof parsedTheme === "object"
        ? parsedTheme as ThemeStorage
        : null;
    }
    catch {
      return null;
    }
  }

  private applySavedTheme(): void {
    this.themeSwitcher.useSystemTheme = this.savedTheme.themeVariantMode === "System";

    if (this.themeSwitcher.useSystemTheme) {
      this.themeSwitcher.setThemeVariantFromSystem();
    }
    else {
      this.themeSwitcher.setThemeVariantByName(this.savedTheme.themeVariantMode as "Light" | "Dark");
    }

    this.loadThemeStyle(this.savedTheme.themeName, true);
  }

  // Expose the useSystemTheme property
  get useSystemTheme(): boolean {
    const rtn = this.themeSwitcher.useSystemTheme;

    return rtn;
  }
  set useSystemTheme(value: boolean) {

    this.themeSwitcher.useSystemTheme = value;
    this.savedTheme.themeVariantMode = value ? "System" : this.savedTheme.themeVariantMode;
    this.saveTheme();
  }

  setThemeVariantByName(themeVariant: "Light" | "Dark") {
    this.themeSwitcher.setThemeVariantByName(themeVariant);
    this.savedTheme.themeVariantMode = themeVariant;
    this.saveTheme();
    //this.settingsService.saveToLocalStorage('themeVariant', themeVariant);
  }

  setThemeVariantByMode(themeVariantMode: "Light" | "Dark" | "System") {

    this.savedTheme.themeVariantMode = themeVariantMode;
    this.themeSwitcher.useSystemTheme = this.savedTheme.themeVariantMode === "System";

    if (!this.useSystemTheme) {
      //this.savedTheme.themeVariant = themeVariantMode;
      this.setThemeVariantByName(themeVariantMode as "Light" | "Dark");  // This will save the theme
    }
    else {
      this.saveTheme();
    }

  }

  setThemeVariantFromSystem() {
    this.themeSwitcher.setThemeVariantFromSystem();
  }


  /**
   * Load the theme by the complete css filename
   * @param css 
   */
  loadThemeByCss(css: string) {
    this.themeSwitcher.loadThemeStyle(css);
  }

  loadThemeStyle(themeName: string, noSave: boolean = false) {

    const theme = resolveTheme(themeName);
    const css = theme?.css;

    if (css) {
      this.savedTheme.themeName = theme?.themeName ?? DEFAULT_THEME_NAME;
      //let css = `${themeName}.theme.styles.css`;

      this.themeSwitcher.loadThemeStyle(css);
      if (!noSave) {
        this.saveTheme();
      }
    }

  }


  private saveTheme() {
    this.settingsService.saveToLocalStorage('savedTheme', JSON.stringify(this.savedTheme));

    // This DOES NOT WORK - signal not received
    //this.savedThemeSignal.set(this.savedTheme);

    // This DOES WORK - makin' copies.
    this.savedThemeSignal.set({ ...this.savedTheme });

  }

}
